'use client';

import { Container } from '..';
import { H2_Lg, P } from './styleConstants';
import styles from './ImagesCarouselSection.module.css';
import { useEffect, useCallback, useState, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { CaretUp } from '../../assets/icons';
import { CustomImage as Image } from '../shared';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';

const CNO_PRO_CAROUSEL_IMAGES = [
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/1.png/w_1600',
    alt: 'Cycle Pattern 1',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/2.png/w_1600',
    alt: 'Cycle Pattern 2',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/3.png/w_1600',
    alt: 'Cycle Pattern 3',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/4.png/w_1600',
    alt: 'Cycle Pattern 4',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/5.png/w_1600',
    alt: 'Cycle Pattern 5',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/6.png/w_1600',
    alt: 'Cycle Pattern 6',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/7.png/w_1600',
    alt: 'Cycle Pattern 7',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/8.png/w_1600',
    alt: 'Cycle Pattern 8',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/9.png/w_1600',
    alt: 'Cycle Pattern 9',
  },
  {
    src: 'web_v2/womens-health/cycle-flags-carousel/10.png/w_1600',
    alt: 'Cycle Pattern 10',
  },
];

const CNO_PLUS_CAROUSEL_IMAGES = [
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/01.png/w_1600',
    alt: 'Cycle Pattern 1',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/02.png/w_1600',
    alt: 'Cycle Pattern 2',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/03.png/w_1600',
    alt: 'Cycle Pattern 3',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/04.png/w_1600',
    alt: 'Cycle Pattern 4',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/05.png/w_1600',
    alt: 'Cycle Pattern 5',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/06.png/w_1600',
    alt: 'Cycle Pattern 6',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/07.png/w_1600',
    alt: 'Cycle Pattern 7',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/08.png/w_1600',
    alt: 'Cycle Pattern 8',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/09.png/w_1600',
    alt: 'Cycle Pattern 9',
  },
  {
    src: 'web_v2/womens-health/plus/cycle-flags-carousel/10.png/w_1600',
    alt: 'Cycle Pattern 10',
  },
];

export const ImagesCarouselSection = ({
  className,
}: {
  className?: string;
}) => {
  const { getValue } = useIsCnOPlus();

    const carouselImages = useMemo(
      () => getValue(CNO_PRO_CAROUSEL_IMAGES, CNO_PLUS_CAROUSEL_IMAGES),
      [getValue],
    );

    const [emblaRef, emblaApi] = useEmblaCarousel({
      loop: true,
      align: 'center',
      slidesToScroll: 1,
      containScroll: 'trimSnaps',
    });

    const [, setActiveSlide] = useState<number>(0);

    const scrollPrev = useCallback(() => {
      if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    const onSelect = useCallback(() => {
      if (!emblaApi) return;
      setActiveSlide(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
      if (!emblaApi) return;
      onSelect();
      emblaApi.on('select', onSelect);
      return () => {
        emblaApi.off('select', onSelect);
      };
    }, [emblaApi, onSelect]);

    const title = getValue(
      "Cycle Flags™ : Your cycle's quietest signals, decoded",
      "Cycle Flags : Your cycle's quietest signals, decoded",
    );
    return (
      <section
        className={
          className
            ? `${styles.imagesCarouselSection} ${className}`
            : styles.imagesCarouselSection
        }
      >
        <Container>
          <div className={styles.textContainer}>
            <H2_Lg className={styles.title}>{title}</H2_Lg>
            <P className={styles.description}>
              Discover unique cycle patterns that help reveal hidden clues about
              your reproductive health
            </P>
          </div>
        </Container>
        <div className={styles.carouselSection}>
          <div className={styles.embla} ref={emblaRef}>
            <div className={styles.embla__container}>
              {carouselImages.map((image, index) => (
                <div className={styles.embla__slide} key={index}>
                  <div className={styles.slideContent}>
                    <Image
                      src={image.src}
                      alt={image.alt}
                      width={1000}
                      height={750}
                      style={{
                        objectFit: 'cover',
                        // borderRadius: '32px',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.carouselControls}>
            <button
              className={styles.embla__prev}
              onClick={scrollPrev}
              aria-label="Previous slide"
            >
              <CaretUp style={{ transform: 'rotate(-90deg)' }} />
            </button>
            <button
              className={styles.embla__next}
              onClick={scrollNext}
              aria-label="Next slide"
            >
              <CaretUp style={{ transform: 'rotate(90deg)' }} />
            </button>
          </div>
        </div>
      </section>
    );
};
